import React, { useMemo } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import FormatQuoteOutlinedIcon from '@mui/icons-material/FormatQuoteOutlined';
import FontDownloadOutlinedIcon from '@mui/icons-material/FontDownloadOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { SelectedService } from '../context/ServiceConfigContext';
import { RequiredInfoField } from '../services/types';
import {
  ENGRAVING_CHARACTER_COUNT_LABEL,
  ENGRAVING_TEXT_LABEL,
} from '../utils/requiredInfo';

const ENGRAVING_LABELS = [
  ENGRAVING_TEXT_LABEL,
  ENGRAVING_CHARACTER_COUNT_LABEL,
  'Engraving Font',
  'Engraving Location',
  'Engraving Position',
  'Previously Engraved',
  'Previous Location',
];

const RING_LABELS = ['Ring Size'];

export const highlightedLabelsForSelection = (selection: SelectedService): Set<string> => {
  const values = selection.requiredInfoValues ?? {};
  const labels = Object.keys(values);
  const result = new Set<string>();

  const hasEngraving = labels.some((label) => ENGRAVING_LABELS.includes(label));
  if (hasEngraving) {
    ENGRAVING_LABELS.forEach((label) => {
      if (values[label]) {
        result.add(label);
      }
    });
  }

  RING_LABELS.forEach((label) => {
    if (values[label]) {
      result.add(label);
    }
  });

  return result;
};

const booleanDisplay = (raw: string | undefined) => {
  if (raw === 'true') return 'Yes';
  if (raw === 'false') return 'No';
  return raw ?? '';
};

const formatValue = (
  label: string,
  raw: string | undefined,
  fieldMap: Map<string, RequiredInfoField>,
) => {
  if (!raw) return undefined;
  const field = fieldMap.get(label);
  if (field?.valueType === 'Boolean') {
    return booleanDisplay(raw);
  }
  return raw;
};

interface ServiceDetailHighlightsProps {
  selection: SelectedService;
  requiredFields: Map<string, RequiredInfoField>;
}

const ServiceDetailHighlights: React.FC<ServiceDetailHighlightsProps> = ({ selection, requiredFields }) => {
  const values = selection.requiredInfoValues ?? {};

  const engravingDetails = useMemo(() => {
    const hasAny = ENGRAVING_LABELS.some((label) => values[label]);
    if (!hasAny) return null;

    const text = formatValue(ENGRAVING_TEXT_LABEL, values[ENGRAVING_TEXT_LABEL], requiredFields);
    const characterCount = formatValue(
      ENGRAVING_CHARACTER_COUNT_LABEL,
      values[ENGRAVING_CHARACTER_COUNT_LABEL],
      requiredFields,
    );

    const detailChips = [
      {
        icon: <FontDownloadOutlinedIcon fontSize="small" />,
        label: 'Font',
        value: formatValue('Engraving Font', values['Engraving Font'], requiredFields),
      },
      {
        icon: <PlaceOutlinedIcon fontSize="small" />,
        label: 'Location',
        value: formatValue('Engraving Location', values['Engraving Location'], requiredFields),
      },
      {
        icon: <LayersOutlinedIcon fontSize="small" />,
        label: 'Position',
        value: formatValue('Engraving Position', values['Engraving Position'], requiredFields),
      },
      {
        icon: <CheckCircleOutlineIcon fontSize="small" />,
        label: 'Previously Engraved',
        value: formatValue('Previously Engraved', values['Previously Engraved'], requiredFields),
      },
      {
        icon: <PlaceOutlinedIcon fontSize="small" />,
        label: 'Previous Location',
        value: formatValue('Previous Location', values['Previous Location'], requiredFields),
      },
      {
        icon: <StraightenOutlinedIcon fontSize="small" />,
        label: 'Characters',
        value: characterCount,
      },
    ].filter((chip) => chip.value);

    return { text, detailChips };
  }, [requiredFields, values]);

  const ringSize = useMemo(
    () => RING_LABELS.map((label) => formatValue(label, values[label], requiredFields)).find(Boolean),
    [requiredFields, values],
  );

  if (!engravingDetails && !ringSize) {
    return null;
  }

  return (
    <Stack spacing={1.25} sx={{ mt: 1.5 }}>
      {engravingDetails && (
        <Box
          sx={(theme) => ({
            borderRadius: 2,
            p: 1.5,
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.35)}`,
            background:
              theme.palette.mode === 'light'
                ? `linear-gradient(135deg, ${alpha(theme.palette.secondary.light, 0.18)} 0%, ${alpha(
                  theme.palette.background.paper,
                  0.92,
                )} 100%)`
                : alpha(theme.palette.secondary.dark, 0.3),
          })}
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <FormatQuoteOutlinedIcon fontSize="small" color="secondary" />
              <Typography variant="overline" color="secondary">
                Engraving Details
              </Typography>
            </Stack>
            {engravingDetails.text && (
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                &ldquo;{engravingDetails.text}&rdquo;
              </Typography>
            )}
            {engravingDetails.detailChips.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap={'wrap'} useFlexGap>
                {engravingDetails.detailChips.map((chip) => (
                  <Chip
                    key={`${chip.label}-${chip.value}`}
                    icon={chip.icon}
                    size="small"
                    label={`${chip.label}: ${chip.value}`}
                    sx={{ borderRadius: 1.5 }}
                    variant="outlined"
                    color="secondary"
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </Box>
      )}

      {ringSize && (
        <Box
          sx={(theme) => ({
            borderRadius: 2,
            py: 1.25,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            background:
              theme.palette.mode === 'light'
                ? alpha(theme.palette.primary.light, 0.12)
                : alpha(theme.palette.primary.dark, 0.32),
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          })}
        >
          <StraightenOutlinedIcon fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight={600}>
            Ring Size
          </Typography>
          <Chip
            label={ringSize}
            size="small"
            color="primary"
            sx={{ fontWeight: 600, borderRadius: 1.5 }}
            variant="filled"
          />
        </Box>
      )}
    </Stack>
  );
};

export default ServiceDetailHighlights;
